import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { ShellRail, useShellUI } from '../../shell/AppShell';
import { rateMyTradeAPI } from '../../utils/api';
import api from '../../utils/api';
import TradeSides from './TradeSides';
import { readVotes, recordVote, sideLabels, votePct } from './rmtShared';
import { leagueFormatPills, recordContext, teamRank, RosterAccordion, SeasonRecap } from './rmtContext';
import { SleeperAvatar } from '../../shell/SleeperAvatar';
import UpsellCard from '../../shell/UpsellCard';
import { ReviewIcon } from '../../shell/icons';

/**
 * RateMyTradeView — the public detail/vote page at /rate-my-trade/:shareId.
 * PATH IS IMMUTABLE (social shares + og images key on it). Anonymous voting
 * works; signed-in extras: claim, analyst verdict in the rail.
 */

const RateMyTradeView = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useShellUI();

  const [trade, setTrade] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [votes, setVotes] = useState(readVotes);
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    let active = true;
    rateMyTradeAPI
      .getByShareId(shareId)
      .then((res) => { if (active) setTrade(res.data?.trade || res.data); })
      .catch(() => { if (active) setLoadError(true); });
    api
      .get(`/rate-my-trade/${shareId}/analysis`)
      .then((res) => { if (active && res.data) setAnalysis(res.data.analysis || res.data); })
      .catch(() => {});
    return () => { active = false; };
  }, [shareId]);

  const myVote = votes[shareId];

  const castVote = async (vote) => {
    if (myVote) return;
    setVotes(recordVote(shareId, vote));
    setTrade((t) => ({
      ...t,
      yesCount: Number(t.yesCount || 0) + (vote === 'yes' ? 1 : 0),
      noCount: Number(t.noCount || 0) + (vote === 'no' ? 1 : 0),
    }));
    try {
      await rateMyTradeAPI.vote(shareId, vote);
    } catch {
      toast('Vote did not land — try again');
    }
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(window.location.href).then(
      () => toast('Link copied'),
      () => toast('Copy failed')
    );
  };

  if (loadError) {
    return (
      <div className="ft-view">
        <span className="ft-k brand">Rate My Trade</span>
        <h1 className="ft-hero">Trade not found.</h1>
        <button className="ft-btn sm ghost" style={{ marginTop: 16 }} onClick={() => navigate('/rate-my-trade')}>
          Back to the ballot
        </button>
      </div>
    );
  }
  if (!trade) {
    return <div className="ft-view"><div className="ft-loading">Pulling the trade…</div></div>;
  }

  const [labelA, labelB] = sideLabels(trade);
  const { total, send } = votePct(trade);
  const { teamA = {}, teamB = {}, allTeams = [], league = {} } = trade.cachedTeamData || {};
  const { getRecord, recordSeason } = recordContext(allTeams, league);
  const rankA = teamRank(teamA, allTeams, getRecord);
  const rankB = teamRank(teamB, allTeams, getRecord);
  const formatPills = leagueFormatPills(league);
  const openIntel = (ownerName) => navigate(`/manager-intel/${String(ownerName).toLowerCase()}`);
  const verdictText = analysis
    ? analysis.verdict || analysis.headline || analysis.summary || analysis.call
    : null;
  const verdictDetail = analysis
    ? analysis.explanation || analysis.reasoning || analysis.detail || analysis.note
    : null;

  return (
    <div className="ft-view" style={{ maxWidth: 860 }}>
      <Helmet><title>{labelA} trade — Rate My Trade — Fantasy Taco</title></Helmet>

      <ShellRail>
        {(teamA.ownerName || teamB.ownerName) && (
          <div className="ft-railsec">
            <span className="ft-k">Scout the managers</span>
            {[teamA, teamB].filter((t) => t.ownerName).map((t) => (
              <button key={t.ownerName} className="ft-railaction" onClick={() => openIntel(t.ownerName)}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                  <SleeperAvatar id={t.avatar} name={t.ownerName} size={24} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.ownerName}</span>
                </span>
              </button>
            ))}
          </div>
        )}
        <div className="ft-railsec">
          <span className="ft-k">Share it</span>
          <button className="ft-railaction" style={{ marginTop: 10 }} onClick={copyLink}>
            <span>Copy the link</span>
          </button>
          <button
            className="ft-railaction"
            onClick={() => window.open(
              `https://twitter.com/intent/tweet?text=${encodeURIComponent('Rate this dynasty trade')}&url=${encodeURIComponent(window.location.href)}`,
              '_blank'
            )}
          >
            <span>Post to X</span>
          </button>
          <button
            className="ft-railaction"
            onClick={() => {
              const apiBase = process.env.REACT_APP_ENV === 'local'
                ? `http://localhost:${process.env.REACT_APP_API_PORT || '3000'}/api`
                : '/api';
              window.open(`${apiBase}/rate-my-trade/${shareId}/og-image.png`, '_blank');
            }}
          >
            <span>View the share image</span>
          </button>
        </div>
        {verdictText && (
          <div className="ft-railsec">
            <span className="ft-k">Analyst Mode</span>
            <div style={{ borderLeft: '3px solid var(--ft-green)', background: 'var(--ft-green-soft)', borderRadius: '0 14px 14px 0', padding: '13px 16px', marginTop: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ft-green-ink)' }}>{String(verdictText)}</div>
              {verdictDetail ? (
                <div style={{ fontSize: 13, color: 'var(--ft-ink-2)', marginTop: 5, lineHeight: 1.5 }}>{String(verdictDetail)}</div>
              ) : null}
            </div>
          </div>
        )}
        {user && (
          <div className="ft-railsec">
            <span className="ft-k">Yours?</span>
            <button
              className="ft-railaction"
              style={{ marginTop: 10 }}
              onClick={async () => {
                try {
                  await rateMyTradeAPI.claim({ shareId });
                  toast('Claimed — it shows under your trades now');
                } catch (err) {
                  toast(err?.response?.data?.error || 'Claim failed');
                }
              }}
            >
              <span>Claim this trade</span>
            </button>
          </div>
        )}
      </ShellRail>

      <div style={{ marginBottom: 18 }}>
        <button className="ft-btn sm ghost" onClick={() => navigate('/rate-my-trade')}>
          Back to the ballot
        </button>
      </div>

      <span className="ft-k brand">Rate My Trade</span>
      <h1 className="ft-hero" style={{ fontSize: 28 }}>Should {labelA} do this?</h1>
      <div className="ft-pillrow" style={{ margin: '10px 0 24px' }}>
        <span className="ft-chip">{trade.leagueName || league.name || 'Dynasty league'}</span>
        {formatPills.map((pill) => <span key={pill} className="ft-chip outline">{pill}</span>)}
        {trade.creatorDisplayName ? <span className="ft-chip outline">by {trade.creatorDisplayName}</span> : null}
        {trade.mode === 'potential' ? <span className="ft-chip outline">Hypothetical</span> : null}
      </div>

      <div className="ft-card white" style={{ padding: 26, maxWidth: 720 }}>
        <TradeSides
          trade={trade}
          teamA={trade.cachedTeamData?.teamA}
          teamB={trade.cachedTeamData?.teamB}
          labelA={`${labelA} receives`}
          labelB={`${labelB} receives`}
          metaA={rankA ? { rank: rankA, totalTeams: allTeams.length, recordSeason } : { recordSeason }}
          metaB={rankB ? { rank: rankB, totalTeams: allTeams.length, recordSeason } : { recordSeason }}
          onOwner={openIntel}
        />
        <div style={{ height: 8, borderRadius: 980, background: '#EBEBED', overflow: 'hidden', marginTop: 20 }}>
          <div style={{ width: `${total ? send : 0}%`, height: '100%', background: 'var(--ft-green)', borderRadius: 980, transition: 'width .5s var(--ft-ease)' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--ft-ink-3)', marginTop: 7 }}>
          <span>{total ? `Send it — ${send}%` : 'No votes yet'}</span>
          <span>{total} vote{total === 1 ? '' : 's'}{myVote ? ' · you voted' : ''}</span>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button className="ft-btn" disabled={!!myVote} onClick={() => castVote('yes')}>
            {myVote === 'yes' ? 'You said send it' : 'Send it'}
          </button>
          <button className="ft-btn ghost" disabled={!!myVote} onClick={() => castVote('no')}>
            {myVote === 'no' ? 'You passed' : 'Pass'}
          </button>
        </div>
      </div>

      {myVote && (
        <div className="ft-section" style={{ maxWidth: 720 }}>
          <UpsellCard
            wordmark="Analyst Review"
            icon={ReviewIcon}
            title="Want an expert's take?"
            body="A real dynasty analyst breaks down this trade — value, timeline fit, what it means long-term."
            cta="Get it reviewed"
            to="/analyst/fantasynerdboi"
          />
        </div>
      )}

      {/* Full rosters — both sides, starters/bench/picks */}
      {(teamA.players?.length > 0 || teamB.players?.length > 0) && (
        <div className="ft-section" style={{ maxWidth: 720 }}>
          <span className="ft-k" style={{ display: 'block', marginBottom: 6 }}>Rosters</span>
          {teamA.ownerName ? <RosterAccordion team={teamA} label={teamA.ownerName} league={league} /> : null}
          {teamB.ownerName ? <RosterAccordion team={teamB} label={teamB.ownerName} league={league} /> : null}
        </div>
      )}

      {/* League season recap — playoffs + regular season standings */}
      <SeasonRecap
        allTeams={allTeams}
        league={league}
        trade={trade}
        getRecord={getRecord}
        recordSeason={recordSeason}
        onOwner={openIntel}
      />
    </div>
  );
};

export default RateMyTradeView;
